//
// # ConstraintGroupSection
//
// Collapsible section for a constraint group.
// Shows group name, direction badge (vertical/horizontal),
// and a list of ConstraintMatchRow children.
//
// Click on the group header highlights all instances across all matches.
//

import React, { useCallback, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, Play, Trash2 } from "lucide-react";
import type { schema } from "GenAlphaSchema";
import { useWorkspace } from "../../context";
import { ConstraintMatchRow } from "./ConstraintMatchRow";

interface ConstraintGroupSectionProps {
  readonly group: schema.schematic.ConstraintGroup;
}

export function ConstraintGroupSection({ group }: ConstraintGroupSectionProps) {
  const workspace = useWorkspace();
  const [collapsed, setCollapsed] = useState(false);

  const matches = group.matches ?? [];

  // Click on group header: select all instances across all matches in the group
  const handleHeaderClick = useCallback(() => {
    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    if (content.contentKind !== "schematic") return;

    // Collect all instance names from all matches in this group
    const names = new Set<string>();
    for (const match of matches) {
      for (const name of match.firstInstances) names.add(name);
      for (const name of match.secondInstances) names.add(name);
    }

    content.deselect();

    for (const instance of content.instances) {
      if (names.has(instance.data.name)) {
        content.addToSelection(instance);
      }
    }
  }, [workspace, matches]);

  const toggleCollapse = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsed((prev) => !prev);
  }, []);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  // Close context menu when clicking outside or pressing Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setContextMenu(null);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [contextMenu]);

  // Delete entire group and all its constraints
  const handleDeleteGroup = useCallback(() => {
    if (group.id === undefined) return;
    setContextMenu(null);
    void workspace.removeConstraintGroup(group.id);
  }, [workspace, group.id]);

  // Place the group's constraints
  const handlePlaceGroup = useCallback(() => {
    if (group.id === undefined) return;
    setContextMenu(null);
    // Select all match IDs in this group, then place them
    const matchIds = matches
      .map((m) => m.id)
      .filter((id): id is number => id !== undefined);
    if (matchIds.length > 0) {
      // Select all matches in this group
      for (const id of matchIds) {
        workspace.selectConstraintMatch(id, true);
      }
      void workspace.placeSelectedConstraints();
    }
  }, [workspace, group.id, matches]);

  // Check if we're viewing a layout
  const editor = workspace.getActiveEditor();
  const isLayout = editor?.content.contentKind === "layout";

  const toggleDirection = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (group.id !== undefined) {
        void workspace.toggleGroupSymmetryDirection(
          group.id,
          group.symmetryDirection
        );
      }
    },
    [workspace, group.id, group.symmetryDirection]
  );

  const label = group.description ?? group.id;

  return (
    <div className="mb-1">
      {/* Group header */}
      <div
        className="constraint-group-header flex items-center gap-1 px-2 py-1"
        onClick={handleHeaderClick}
        onContextMenu={handleContextMenu}
      >
        <button
          className="ide-panel-header-btn"
          onClick={toggleCollapse}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
        </button>
        <span className="text-[11px] font-semibold text-[var(--color-ide-text)] flex-1 truncate">
          {label}
        </span>
        <span
          className="constraint-direction-badge cursor-pointer hover:opacity-80"
          onClick={toggleDirection}
          title="Click to toggle symmetry direction"
        >
          {group.symmetryDirection === "vertical" ? "vert" : "horiz"}
        </span>
        <span className="text-[10px] text-[var(--color-ide-text-dim)]">
          {matches.length}
        </span>
      </div>

      {/* Match rows */}
      {!collapsed && (
        <div className="flex flex-col gap-1 pl-5 pr-2 pt-1">
          {matches.map((match) => (
            <ConstraintMatchRow key={match.id} match={match} />
          ))}
        </div>
      )}

      {/* Context menu */}
      {contextMenu &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-50 py-1 rounded shadow-lg border text-xs min-w-[120px]"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
              backgroundColor: "var(--color-ide-surface)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            {isLayout && (
              <button
                className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
                onClick={handlePlaceGroup}
              >
                <Play size={12} />
                Place Group
              </button>
            )}
            <button
              className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
              style={{ color: "var(--color-ide-error, #e53e3e)" }}
              onClick={handleDeleteGroup}
            >
              <Trash2 size={12} />
              Delete Group
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}
